import PageShell from "@/components/feature/PageShell";
import ScrollReveal from "@/components/feature/ScrollReveal";
import { Link } from "react-router-dom";
import { useModal } from "@/hooks/useModal";

const offerings = [
  {
    icon: "ri-cloud-line",
    title: "Cloud-Native Development",
    desc: "We design and build applications that leverage the full power of the cloud - scalable, resilient, and cost-efficient from day one.",
    items: ["Custom SaaS & business platforms", "Cloud-native (AWS, Azure) & API-first", "Legacy modernisation & robust architecture", "Full-stack engineering: React, .NET, Python, Go"],
  },
  {
    icon: "ri-code-s-slash-line",
    title: "Custom SaaS Platforms",
    desc: "End-to-end SaaS product development - from concept to production. We have shipped platforms used by thousands of users across Africa.",
    items: ["Multi-tenant architecture", "Subscription & billing engines", "White-label capabilities", "Custom SaaS & business platforms"],
  },
  {
    icon: "ri-link",
    title: "API Design & Integration",
    desc: "Robust REST and GraphQL APIs that connect your systems to the world. Secure, versioned, and well-documented.",
    items: ["API-first architecture", "Third-party integrations", "Developer portals & docs", "Cloud-native (AWS, Azure) & API-first"],
  },
  {
    icon: "ri-stack-line",
    title: "Full-Stack Engineering",
    desc: "Modern frontends paired with high-performance backends. React, Node.js, Python, Go - we choose the right stack for the job.",
    items: ["React / Vue / Angular frontends", "Node.js / Python / Go backends", "Database design & optimization", "Full-stack engineering: React, .NET, Python, Go"],
  },
];

const techStack = [
  "React",
  "TypeScript",
  "Node.js",
  "Python",
  "Go",
  "PostgreSQL",
  "MongoDB",
  "AWS",
  "Docker",
  "Kubernetes",
];

export default function SoftwarePage() {
  const { openQuote } = useModal();

  return (
    <PageShell>
      <section className="relative w-full pt-28 md:pt-36 pb-16 md:pb-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8 mb-8">
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-brand-500 transition-colors">
            <i className="ri-arrow-left-line" /> Back to Services
          </Link>
        </div>
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                Software Engineering
              </span>
              <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4">
                Build Software That Scales
              </h1>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
                Modern, scalable web and mobile applications. We follow DevSecOps principles from design to deployment.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {offerings.map((o, i) => (
              <ScrollReveal key={o.title} delay={i + 1}>
                <div className="group p-6 md:p-8 rounded-2xl bg-white dark:bg-dark-bg border border-gray-100 dark:border-dark-border hover:border-brand-200 dark:hover:border-brand-500/30 transition-all duration-300 h-full">
                  <div className="w-12 h-12 rounded-xl bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${o.icon} text-xl text-brand-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {o.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                    {o.desc}
                  </p>
                  <ul className="space-y-2">
                    {o.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                        <i className="ri-check-line text-brand-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-white dark:bg-dark-bg">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-14 max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Technology Stack
              </h2>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                We are technology-agnostic and choose the right tools for each project.
              </p>
            </div>
          </ScrollReveal>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 max-w-3xl mx-auto">
            {techStack.map((t) => (
              <div
                key={t}
                className="px-5 py-2.5 rounded-full bg-gray-50 dark:bg-dark-card border border-gray-200 dark:border-dark-border text-sm font-semibold text-gray-700 dark:text-gray-300"
              >
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center max-w-5xl mx-auto">
            <ScrollReveal>
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                Case Study
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                AHIS - Healthcare SaaS Built From Scratch
              </h2>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                AHIS is our flagship product - a cloud-native hospital management platform serving healthcare facilities across Africa. Built with React, Node.js, and PostgreSQL, it handles thousands of patient records daily.
              </p>
              <Link
                to="/ahis"
                className="inline-flex items-center gap-1.5 px-6 py-3 rounded-full bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Explore AHIS <i className="ri-arrow-right-line text-xs" />
              </Link>
            </ScrollReveal>
            <ScrollReveal>
              <div className="p-8 rounded-2xl bg-white dark:bg-dark-bg border border-gray-100 dark:border-dark-border">
                <div className="grid grid-cols-2 gap-6 text-center">
                  {[
                    { label: "Hospitals Served", value: "50+" },
                    { label: "Patient Records", value: "2M+" },
                    { label: "Uptime", value: "99.9%" },
                    { label: "Countries", value: "4" },
                  ].map((stat) => (
                    <div key={stat.label}>
                      <div className="text-2xl md:text-3xl font-extrabold text-brand-500 mb-1">
                        {stat.value}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-brand-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Ready to Build Something Great?
          </h2>
          <p className="text-sm md:text-base text-brand-100 max-w-lg mx-auto mb-8">
            Tell us about your project and we will help you bring it to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={openQuote}
              className="px-8 py-3.5 rounded-full bg-white text-brand-600 text-sm font-semibold hover:bg-brand-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
            >
              Start a Project
            </button>
            <Link
              to="/services"
              className="px-8 py-3.5 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold hover:bg-white/20 transition-all border border-white/20 whitespace-nowrap text-center cursor-pointer"
            >
              All Services
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}